import { useTranslation } from 'react-i18next'

import {
  danas,
  imeMjeseca,
  imenaDana,
  mreza,
  pomakMjeseca,
  razloziDatum,
  uRasponu,
  type IsoDatum,
} from './kalendar'
import { razredDana, razredKalendara, razredPrigusen, type Ton } from './ton'

/**
 * MREŽA MJESECA — jedini ekran na kojem osoblje bira dan (D15-A §4).
 *
 * Otvara se **ispod polja i gura sadržaj**, umjesto da lebdi nad njim. Razlog je
 * praktičan: polja datuma najčešće stoje u dijalogu čije se TIJELO skrola
 * (sticky podnožje, §5), a plutajući sloj bi se ondje odrezao o rub tijela — i to
 * baš na zadnjem retku obrasca, gdje se datum obično i nalazi.
 *
 * Dan izvan raspona (`min`/`max`) se PRIKAZUJE, ali se ne bira: mjesec kojemu
 * polovica dana nedostaje čita se kao pokvaren kalendar, a ugašen dan kaže
 * "postoji, ali ne za ovo".
 */
export function Kalendar({
  ton,
  vrijednost,
  mjesec,
  naMjesec,
  naIzbor,
  min,
  max,
}: {
  ton: Ton
  /** Trenutno izabran dan; prazno kad izbora nema. */
  vrijednost: string
  /** Koji se mjesec crta (drži ga polje, jer preživljava zatvaranje). */
  mjesec: { godina: number; mjesec: number }
  naMjesec: (novi: { godina: number; mjesec: number }) => void
  naIzbor: (dan: IsoDatum) => void
  min?: string
  max?: string
}) {
  const { t, i18n } = useTranslation()

  const jezik = i18n.language
  const danasnji = danas()
  const prigusen = razredPrigusen(ton)

  return (
    <div className={razredKalendara(ton)} data-kalendar="mjesec">
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label={t('common.previous')}
          className={`touch-target rounded-lg px-3 text-lg ${prigusen}`}
          onClick={() => naMjesec(pomakMjeseca(mjesec.godina, mjesec.mjesec, -1))}
        >
          ‹
        </button>

        {/* `aria-live`: promjena mjeseca strelicom mora biti čujna, ne samo vidljiva. */}
        <span aria-live="polite" className="text-sm font-medium">
          {imeMjeseca(mjesec.godina, mjesec.mjesec, jezik)}
        </span>

        <button
          type="button"
          aria-label={t('common.next')}
          className={`touch-target rounded-lg px-3 text-lg ${prigusen}`}
          onClick={() => naMjesec(pomakMjeseca(mjesec.godina, mjesec.mjesec, 1))}
        >
          ›
        </button>
      </div>

      <table className="w-full table-fixed border-separate border-spacing-0.5 text-center text-sm">
        <thead>
          <tr>
            {imenaDana(jezik).map((ime) => (
              <th key={ime} scope="col" className={`pb-1 text-xs font-normal ${prigusen}`}>
                {ime}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mreza(mjesec.godina, mjesec.mjesec).map((tjedan, redni) => (
            <tr key={redni}>
              {tjedan.map((dan, stupac) => {
                if (dan === null) return <td key={stupac} />

                const izabran = dan === vrijednost
                const broj = razloziDatum(dan)?.dan ?? ''

                return (
                  <td key={stupac}>
                    <button
                      type="button"
                      disabled={!uRasponu(dan, min, max)}
                      // Ime je PUN datum, ne broj: čitač ekrana bez toga izgovara
                      // "14" bez ijedne naznake kojeg je to mjeseca.
                      aria-label={dan}
                      aria-current={dan === danasnji ? 'date' : undefined}
                      aria-pressed={izabran}
                      onClick={() => naIzbor(dan)}
                      className={`h-9 w-full rounded-lg text-sm disabled:cursor-not-allowed disabled:opacity-40 ${razredDana(
                        ton,
                        izabran,
                        dan === danasnji,
                      )}`}
                    >
                      {broj}
                    </button>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
